import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { isLang, resolveInitialLang } from '../lib/langPrefs';

interface Props {
  children: ReactNode;
}

export function LanguageProvider({ children }: Props) {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isLang(lang) && i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
    if (isLang(lang)) {
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  if (!isLang(lang)) {
    // The matched :lang segment is not a supported language (e.g. "/products"
    // matched as lang="products"). Treat as a legacy unprefixed path: preserve
    // the full original URL and prepend the resolved language prefix.
    const targetLang = resolveInitialLang(navigator.language);
    const target = `/${targetLang}${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
